import React, { useState, useEffect } from 'react';
import { Calendar, dateFnsLocalizer } from 'react-big-calendar';
import format from 'date-fns/format';
import parse from 'date-fns/parse';
import startOfWeek from 'date-fns/startOfWeek';
import getDay from 'date-fns/getDay';
import enUS from 'date-fns/locale/en-US';
import { Box, Container, Typography, Paper, Button, Dialog, DialogTitle, DialogContent, DialogActions, TextField, MenuItem, Chip, Alert, CircularProgress } from '@mui/material';
import 'react-big-calendar/lib/css/react-big-calendar.css';
import { fetchClasses, bookClass } from '../api/client';
import { useNavigate, useLocation } from 'react-router-dom';

const locales = {
    'en-US': enUS,
};

const localizer = dateFnsLocalizer({
    format,
    parse,
    startOfWeek,
    getDay,
    locales,
});

const tracks = ['Right Brain Intensive', 'Little Polyglot', 'Heart & Harmony', 'Preschool Prep'];

const SchedulePage = () => {
    const navigate = useNavigate();
    const location = useLocation();
    const [courses, setCourses] = useState([]);
    const [events, setEvents] = useState([]);
    const [user, setUser] = useState(null);
    const [loading, setLoading] = useState(true);
    const [error, setError] = useState(null);

    // Dialog States
    const [openDetail, setOpenDetail] = useState(false);
    const [selectedEvent, setSelectedEvent] = useState(null);

    useEffect(() => {
        loadData();
        const storedUser = localStorage.getItem('user');
        if (storedUser) setUser(JSON.parse(storedUser));
    }, []);

    // Check for pending booking after login/register
    useEffect(() => {
        const pendingId = localStorage.getItem('pendingBookingEventId');
        if (pendingId && events.length > 0 && user) {
            const eventToBook = events.find(e => e.id === parseInt(pendingId));
            if (eventToBook) {
                setSelectedEvent(eventToBook);
                setOpenDetail(true);
                localStorage.removeItem('pendingBookingEventId'); // Clear it
            }
        }
    }, [events, user]);

    const loadData = async () => {
        setLoading(true);
        try {
            const data = await fetchClasses();
            setCourses(data);
            // Transform for Calendar
            const mappedEvents = data.map(c => ({
                id: c.id,
                title: c.title,
                start: new Date(c.schedule_time),
                end: new Date(new Date(c.schedule_time).getTime() + (c.duration_minutes || 60) * 60000),
                resource: c
            }));
            setEvents(mappedEvents);
        } catch (err) {
            console.error(err);
            setError("Failed to load schedule");
        } finally {
            setLoading(false);
        }
    };

    const handleSelectSlot = ({ start }) => {
        if (user?.role !== 'admin') return;
        setSelectedSlot(start);
        setOpenAdd(true);
    };

    const handleSelectEvent = (event) => {
        setSelectedEvent(event);
        setOpenDetail(true);
    };

    const handleBook = async () => {
        if (!user) {
            // Save intent and redirect to register
            if (selectedEvent) {
                localStorage.setItem('pendingBookingEventId', selectedEvent.id);
            }
            navigate('/register');
            return;
        }

        if (!selectedEvent) return;

        try {
            const result = await bookClass(selectedEvent.id, user.id, false);
            alert(result.message || "Booking Successful!");
            setOpenDetail(false);
        } catch (err) {
            if (err.full) {
                const confirmWait = window.confirm("Class is full! Join waitlist?");
                if (confirmWait) {
                    await bookClass(selectedEvent.id, user.id, true);
                    alert("Joined Waitlist");
                    setOpenDetail(false);
                }
            } else {
                alert(err.message || "Booking Failed");
            }
        }
    };

    return (
        <Box sx={{ py: 4, bgcolor: '#f4f6f8', minHeight: '90vh' }}>
            <Container maxWidth="xl">
                <Typography variant="h3" gutterBottom sx={{ fontWeight: 800, color: 'primary.main' }}>
                    Class Schedule
                </Typography>

                {error && <Alert severity="error" sx={{ mb: 2 }}>{error}</Alert>}

                <Paper sx={{ p: 2, height: '80vh', borderRadius: 4 }}>
                    {loading ? <CircularProgress /> : (
                        <Calendar
                            localizer={localizer}
                            events={events}
                            startAccessor="start"
                            endAccessor="end"
                            style={{ height: '100%' }}
                            onSelectEvent={handleSelectEvent}
                            views={['month', 'week', 'day']}
                            defaultView='week'
                        />
                    )}
                </Paper>



                {/* Event Detail Dialog */}
                <Dialog open={openDetail} onClose={() => setOpenDetail(false)}>
                    <DialogTitle>{selectedEvent?.title}</DialogTitle>
                    <DialogContent>
                        <Typography variant="subtitle1" color="primary" gutterBottom>
                            {selectedEvent?.resource.track}
                        </Typography>
                        <Typography variant="body1" paragraph>
                            {selectedEvent?.resource.description}
                        </Typography>
                        <Typography variant="body2">
                            <strong>Time:</strong> {selectedEvent?.start.toLocaleString()}
                        </Typography>
                        <Typography variant="body2">
                            <strong>Instructor:</strong> {selectedEvent?.resource.instructor}
                        </Typography>
                        <Typography variant="body2">
                            <strong>Price:</strong> {selectedEvent?.resource.price_credits} Credit(s)
                        </Typography>
                        <Chip
                            label="Spaces Available"
                            color="success"
                            size="small"
                            sx={{ mt: 2 }}
                        />
                    </DialogContent>
                    <DialogActions>

                        <Button onClick={() => setOpenDetail(false)}>Close</Button>
                        <Button onClick={handleBook} variant="contained" color="secondary">
                            {user ? "Book Class" : "Register to Book"}
                        </Button>
                    </DialogActions>
                </Dialog>
            </Container>
        </Box>
    );
};

export default SchedulePage;
